/**
 * Discover / mutual dating compatibility helpers.
 *
 * DB truth (from repo; production JSON not sampled here):
 * - users.gender: string | null (no JSON constraint in generated types; app uses male | female | other | prefer-not-to-say).
 * - users.dating_preferences: Json — app writes gender_preference: string[] via EditProfileModal
 *   (canonical tokens: male, female, other, everyone — see EditProfileModal).
 *
 * Legacy read: some rows may use plural key gender_preferences in JSON (never written by current app).
 * We do NOT merge gender_pref inside dating_preferences — only gender_preference is written there.
 *
 * Matching is strict: trim + lowercase equality, or "everyone" (case-insensitive).
 * No synonym expansion (women/men/etc.) unless proven in production data.
 *
 * To verify stored values in Supabase (CTO / read-only):
 *   SELECT DISTINCT gender FROM users WHERE gender IS NOT NULL;
 *   SELECT DISTINCT elem FROM users, LATERAL jsonb_array_elements_text(dating_preferences->'gender_preference') AS elem
 *     WHERE dating_preferences ? 'gender_preference';
 */

const EVERYONE_LC = 'everyone';

function comparable(s: string | null | undefined): string | null {
  if (s == null || typeof s !== 'string') return null;
  const t = s.trim().toLowerCase();
  return t || null;
}

/** Exported for guards (e.g. "has gender string after normalize"). */
export function normalizeUserGender(gender: string | null | undefined): string | null {
  return comparable(gender);
}

function toStringArray(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(v => (v == null ? '' : String(v))).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return [raw.trim()];
  }
  return [];
}

/**
 * Preference strings from dating_preferences JSON.
 * Reads gender_preference first, then legacy plural key gender_preferences only.
 */
export function extractGenderPreferenceList(datingPrefs: unknown): string[] {
  if (!datingPrefs || typeof datingPrefs !== 'object') return [];
  const o = datingPrefs as Record<string, unknown>;
  const merged = [...toStringArray(o.gender_preference), ...toStringArray(o.gender_preferences)];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of merged) {
    const key = x.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(x);
  }
  return out;
}

/**
 * True if prefs include everyone (case-insensitive) or an entry equal to target gender (case-insensitive).
 */
export function preferenceListWantsGender(preferenceStrings: string[], targetGender: string | null): boolean {
  const target = comparable(targetGender);
  if (!target) return false;

  for (const pref of preferenceStrings) {
    const p = comparable(pref);
    if (!p) continue;
    if (p === EVERYONE_LC) return true;
    if (p === target) return true;
  }
  return false;
}

export function mutualGenderDatingCompatible(
  viewerDatingPrefs: unknown,
  candidateDatingPrefs: unknown,
  viewerGender: string | null | undefined,
  candidateGender: string | null | undefined
): { viewerWantsCandidate: boolean; candidateWantsViewer: boolean } {
  const vg = comparable(viewerGender ?? undefined);
  const cg = comparable(candidateGender ?? undefined);

  const viewerPrefs = extractGenderPreferenceList(viewerDatingPrefs);
  const candidatePrefs = extractGenderPreferenceList(candidateDatingPrefs);

  return {
    viewerWantsCandidate: preferenceListWantsGender(viewerPrefs, cg),
    candidateWantsViewer: preferenceListWantsGender(candidatePrefs, vg)
  };
}

/** Canonical user.gender tokens written by EditProfileModal. */
const SUPPORTED_USER_GENDERS = new Set([
  'male',
  'female',
  'other',
  'prefer-not-to-say',
]);

/** Canonical dating_preferences.gender_preference tokens written by EditProfileModal. */
const SUPPORTED_GENDER_PREFERENCES = new Set([
  'male',
  'female',
  'other',
  'everyone',
]);

/**
 * Viewer-only Discover match-preference readiness.
 * True when gender normalizes to a supported token and at least one
 * gender preference normalizes to a supported token.
 * Accepts a preference array or a dating_preferences object.
 */
export function areDiscoverMatchPreferencesReady(
  gender: unknown,
  genderPreference: unknown,
): boolean {
  const normalizedGender =
    typeof gender === 'string' || gender == null
      ? normalizeUserGender(gender as string | null | undefined)
      : null;
  if (!normalizedGender || !SUPPORTED_USER_GENDERS.has(normalizedGender)) {
    return false;
  }

  const prefsSource = Array.isArray(genderPreference)
    ? { gender_preference: genderPreference }
    : genderPreference;
  const prefs = extractGenderPreferenceList(prefsSource);
  return prefs.some((pref) => {
    const p = comparable(pref);
    return p != null && SUPPORTED_GENDER_PREFERENCES.has(p);
  });
}
